import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { enrollmentActions } from "../slices/enrollment";
import { getErrorMessage } from "../../utils/errorUtils";
import { EnrollmentRequestDto } from "../../shared/dtos/enrollment/EnrollmentRequestDto";
import { EnrollmentResponseDto } from "../../shared/dtos/enrollment/EnrollmentResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";
import { EnrollmentUpdateRequestDto } from "../../shared/dtos/enrollment/EnrollmentUpdateRequestDto";
import { EnrollmentStatus } from "../../shared/enums/EnrollmentStatus";

export interface EnrollmentQueryParams {
  branchId?: string;
  classroomId?: string;
  academicYearId?: string;
  status?: EnrollmentStatus;
  search?: string;
  page?: number;
  size?: number;
}

export const createEnrollment =
  (values: EnrollmentRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(enrollmentActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<EnrollmentResponseDto>
      >("/enrollments", values, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Enrollment created successfully!");
        dispatch(enrollmentActions.createSuccess(data.data));
        dispatch(
          enrollmentActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(enrollmentActions.setErrorCreate(message));
    }
  };

export const getEnrollments =
  (params: EnrollmentQueryParams = { page: 1, size: 10 }) =>
  async (dispatch: AppDispatch) => {
    dispatch(enrollmentActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<EnrollmentResponseDto>>
      >("/enrollments", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(enrollmentActions.setItems(data.data.content));
        dispatch(enrollmentActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(enrollmentActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getEnrollmentById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(enrollmentActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<EnrollmentResponseDto>
      >(`/enrollments/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(enrollmentActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(enrollmentActions.setErrorFetchOne(getErrorMessage(error)));
    }
  };

export const updateEnrollment =
  (payload: { id: string; values: Partial<EnrollmentUpdateRequestDto> }) =>
  async (dispatch: AppDispatch) => {
    dispatch(enrollmentActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<EnrollmentResponseDto>
      >(`/enrollments/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Enrollment updated successfully!");
        dispatch(enrollmentActions.updateSuccess(data.data));
        dispatch(
          enrollmentActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(enrollmentActions.setErrorUpdate(message));
    }
  };

export const deleteEnrollment =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(enrollmentActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/enrollments/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(data.message || "Enrollment deleted successfully!");
        dispatch(enrollmentActions.deleteSuccess(id));
        dispatch(
          enrollmentActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(enrollmentActions.setErrorDelete(message));
    }
  };

export const resetEnrollmentServerStatus = () => (dispatch: AppDispatch) => {
  dispatch(enrollmentActions.setServerStatus(null));
};
